import React from 'react';
import { Mui } from '@osu/react-ui';
import { useGetScholarsPreferencesQuery, useGetOpenScholarsProgramsQuery } from '../../../services/graphql/api';
import useUserEmplid from '../../hooks/useUserEmplid';

/**
 * has505 — Read-only display of a student's submitted Scholars program preferences.
 * Shown on the dashboard after preference submission and during the review period.
 * Owner-scoped: fetches the logged-in student's own preferences.
 */
export default function ScholarsPreferencesReadOnly() {
    const { emplid } = useUserEmplid();
    const { data: preferences, isLoading, isError } = useGetScholarsPreferencesQuery(
        { applicantEmployeeId: emplid },
        { skip: !emplid }
    );
    // Fetch program list for label resolution
    const { data: programs } = useGetOpenScholarsProgramsQuery();

    if (isLoading) {
        return (
            <Mui.Box sx={{ mt: 2 }}>
                <Mui.Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
            </Mui.Box>
        );
    }

    if (isError || !preferences || preferences.length === 0) {
        return null; // Silently hide if no preferences found (shouldn't happen at this stage)
    }

    // Build label lookup from open programs
    const programLabels = (programs ?? []).reduce((acc, p) => {
        acc[p.subProgramId] = p.label || p.subProgramId;
        return acc;
    }, {});

    const sorted = [...preferences].sort((a, b) => a.rank - b.rank);

    return (
        <Mui.Box sx={{ mt: 3 }}>
            <Mui.Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Your Program Preferences
            </Mui.Typography>
            <Mui.Paper variant="outlined" sx={{ p: 2 }}>
                <Mui.List dense disablePadding>
                    {sorted.map((pref) => (
                        <Mui.ListItem key={pref.rank} disableGutters sx={{ py: 0.5 }}>
                            <Mui.ListItemText
                                primary={`${pref.rank}. ${programLabels[pref.subProgramId] || pref.subProgramId}`}
                                primaryTypographyProps={{ variant: 'body2' }}
                            />
                        </Mui.ListItem>
                    ))}
                </Mui.List>
            </Mui.Paper>
        </Mui.Box>
    );
}
